import {
  createAsyncThunk,
  createSelector,
  createSlice,
  SerializedError,
} from "@reduxjs/toolkit";

import { SearchService } from "services";
import {
  IAPIState,
  IStoreState,
  IErrorPayload,
  ISearchResults,
  ISearchResult,
  IToolSearchData,
} from "types";

export interface IToolSearchState {
  data?: ISearchResult[];
  meta: {
    status: IAPIState;
    error?: SerializedError | null;
    hasSearched?: boolean;
    pagination?: {
      page?: number;
      amount?: number;
      total?: number;
    };
  };
}

const initialState: IToolSearchState = {
  data: undefined,
  meta: {
    status: IAPIState.idle,
    error: null,
    pagination: undefined,
    hasSearched: false,
  },
};

const NB_SEARCH_RESULT_ITEMS = 10;

export const searchTools = createAsyncThunk<
  // Return type of the payload creator
  ISearchResults,
  // First argument to the payload creator
  Partial<IToolSearchData>,
  // Types for ThunkAPI
  { rejectValue: IErrorPayload }
>("toolSearch/searchTools", async (data, { rejectWithValue }) => {
  try {
    return await SearchService.searchTools({
      page: data.page || 1,
      amount: NB_SEARCH_RESULT_ITEMS,
      term: data.term,
      facets: data.facets || {},
    });
  } catch (error) {
    return rejectWithValue(error as IErrorPayload);
  }
});

const toolSearchSlice = createSlice({
  name: "toolSearch",
  initialState,
  reducers: {
    clearToolSearchState: (state) => {
      state.data = [];
      state.meta = initialState.meta;
    },
  },
  extraReducers: (builder) => {
    // search pending
    builder.addCase(searchTools.pending, (state, { meta }) => {
      state.meta = {
        ...state.meta,
        status: IAPIState.pending,
        error: null,
        hasSearched: false,
        pagination: {
          ...state.meta.pagination, // keep current pagination, update page
          page: meta.arg.page || 1,
        },
      };
    });
    // search failed
    builder.addCase(searchTools.rejected, (state, { payload }) => {
      state.meta = {
        status: IAPIState.failed,
        error: payload?.error,
        hasSearched: true,
        pagination: undefined, // remove pagination on error
      };
    });
    // search succeeded
    builder.addCase(searchTools.fulfilled, (state, { payload, meta }) => {
      state.meta = {
        ...state.meta,
        status: IAPIState.succeeded,
        error: null,
        hasSearched: true,
        pagination: {
          page: meta.arg.page || 1, // if no page provided, fallback to  page 1
          amount: NB_SEARCH_RESULT_ITEMS,
          total: payload.total,
        },
      };

      state.data = payload.results;
    });
  },
});

export const toolSearchMetaSelector = createSelector(
  (state: IStoreState) => state.toolSearch,
  (toolSearch) => toolSearch.meta
);

export const toolSearchDataSelector = createSelector(
  (state: IStoreState) => state.toolSearch,
  (toolSearch) => toolSearch.data
);

export const { clearToolSearchState } = toolSearchSlice.actions;
export const toolSearchReducer = toolSearchSlice.reducer;
